/**
 * Prominent multi-field search bar (Task 1). A debounced, clearable text input
 * with a leading search glyph — shared by the queue, shipments and admin lists
 * so search looks and behaves identically everywhere. Token-driven + density-
 * aware (reads the same `.control` sizing as other inputs).
 *
 * Behaviour:
 *  - holds a local draft and emits `onChange` debounced (default 250ms) so we
 *    don't refetch on every keystroke; clearing emits immediately;
 *  - syncs the draft when `value` changes from the outside (e.g. "Clear all");
 *  - Esc clears the field (and stops propagation so it doesn't also close a
 *    surrounding drawer); the clear button is keyboard-operable + labelled;
 *  - optional `focusShortcut` (e.g. "/") focuses the field from anywhere on the
 *    page, unless the user is already typing in a field.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Icon } from "./Icon.js";

export interface SearchBarHandle {
  focus: () => void;
}

export const SearchBar = forwardRef<
  SearchBarHandle,
  {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    ariaLabel?: string;
    /** Debounce before emitting onChange (ms). 0 = immediate. */
    debounceMs?: number;
    /** A single key (e.g. "/") that focuses the field from anywhere on the page. */
    focusShortcut?: string;
    className?: string;
  }
>(function SearchBar(
  { value, onChange, placeholder = "Search…", ariaLabel = "Search", debounceMs = 250, focusShortcut, className },
  ref,
) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }), []);

  // Keep the draft in step when the committed value changes externally.
  useEffect(() => setDraft(value), [value]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const emit = (v: string, immediate = false) => {
    if (timer.current) clearTimeout(timer.current);
    if (immediate || debounceMs <= 0) onChange(v);
    else timer.current = setTimeout(() => onChange(v), debounceMs);
  };

  const clear = () => {
    setDraft("");
    emit("", true);
    inputRef.current?.focus();
  };

  // Optional page-level "/" focus shortcut (ignored while typing elsewhere).
  useEffect(() => {
    if (!focusShortcut) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== focusShortcut || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [focusShortcut]);

  return (
    <div className={`search-bar${className ? ` ${className}` : ""}`} role="search">
      <Icon name="search" size={15} className="search-bar__icon" />
      <input
        ref={inputRef}
        type="text"
        className="control search-bar__input"
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        spellCheck={false}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          emit(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape" && draft) {
            e.stopPropagation();
            clear();
          }
        }}
      />
      {draft && (
        <button type="button" className="search-bar__clear" aria-label="Clear search" onClick={clear}>
          <Icon name="x" size={14} />
        </button>
      )}
    </div>
  );
});
